import type { InteractionBlueprintData } from "./interactionBlueprintSchema";

const asRecord = (v: unknown): Record<string, unknown> =>
  v !== null && typeof v === "object" && !Array.isArray(v)
    ? (v as Record<string, unknown>)
    : {};

/** Normalize API/content payload `content` to a map keyed by form label (or index string). */
export const normalizeRawContentToRecord = (
  rawContent: unknown,
): Record<string, unknown> => {
  if (Array.isArray(rawContent)) {
    return Object.fromEntries(
      rawContent.map((item, i) => [String(i), item] as const),
    );
  }
  if (
    rawContent !== null &&
    typeof rawContent === "object" &&
    !Array.isArray(rawContent)
  ) {
    return rawContent as Record<string, unknown>;
  }
  return {};
};

/**
 * Parsed grids use `cells[]` only; raw blueprints often use singular `cell`. For opaque diff,
 * treat `cell` vs `cells[0]` as the same node when raw has only `cell` (no non-empty `cells`)
 * and parsed has exactly one cell — avoids false `content.*.cell` opaque paths.
 */
const alignParsedGridCellForOpaqueDiff = (
  rawObj: Record<string, unknown>,
  parsedObj: Record<string, unknown>,
): Record<string, unknown> => {
  const rawCell = rawObj.cell;
  const rawCells = rawObj.cells;
  const rawHasCell =
    rawCell !== null && typeof rawCell === "object" && !Array.isArray(rawCell);
  const rawCellsEmpty = !Array.isArray(rawCells) || rawCells.length === 0;
  const parsedCells = parsedObj.cells;
  const parsedSingle =
    Array.isArray(parsedCells) &&
    parsedCells.length === 1 &&
    parsedCells[0] !== null &&
    typeof parsedCells[0] === "object";
  const parsedNoCell = !("cell" in parsedObj);
  if (rawHasCell && rawCellsEmpty && parsedSingle && parsedNoCell) {
    return {
      ...parsedObj,
      cell: parsedCells[0],
    };
  }
  return parsedObj;
};

/**
 * Keys present on `raw` but missing on `parsed`, plus recursive object walks for nested extras.
 */
const diffKeysDroppedFromParsed = (
  raw: Record<string, unknown>,
  parsed: Record<string, unknown>,
  prefix: string,
): string[] => {
  const out: string[] = [];
  for (const key of Object.keys(raw)) {
    if (!(key in parsed)) {
      out.push(`${prefix}.${key}`);
      continue;
    }
    const r = raw[key];
    const p = parsed[key];
    if (
      r !== null &&
      typeof r === "object" &&
      !Array.isArray(r) &&
      p !== null &&
      typeof p === "object" &&
      !Array.isArray(p)
    ) {
      out.push(
        ...diffKeysDroppedFromParsed(
          r as Record<string, unknown>,
          p as Record<string, unknown>,
          `${prefix}.${key}`,
        ),
      );
    }
    if (Array.isArray(r) && Array.isArray(p) && r.length === p.length) {
      for (let i = 0; i < r.length; i++) {
        const re: unknown = r[i];
        const pe: unknown = p[i];
        if (
          re !== null &&
          typeof re === "object" &&
          !Array.isArray(re) &&
          pe !== null &&
          typeof pe === "object" &&
          !Array.isArray(pe)
        ) {
          out.push(
            ...diffKeysDroppedFromParsed(
              re as Record<string, unknown>,
              pe as Record<string, unknown>,
              `${prefix}.${key}[${i}]`,
            ),
          );
        }
      }
    }
  }
  return out;
};

/**
 * Paths to fields present on the inbound payload but lost or narrowed after Zod/content parsing.
 */
export const collectOpaqueFieldPaths = (
  interactionBlueprintRaw: unknown,
  parsed: InteractionBlueprintData,
): string[] => {
  const paths: string[] = [];
  if (
    interactionBlueprintRaw == null ||
    typeof interactionBlueprintRaw !== "object" ||
    Array.isArray(interactionBlueprintRaw)
  ) {
    return paths;
  }

  const root = interactionBlueprintRaw as Record<string, unknown>;

  if (parsed.metadata != null && root.metadata != null) {
    paths.push(
      ...diffKeysDroppedFromParsed(
        asRecord(root.metadata),
        JSON.parse(JSON.stringify(parsed.metadata)) as Record<string, unknown>,
        "metadata",
      ),
    );
  }

  const rawContentMap = normalizeRawContentToRecord(root.content);
  const parsedByLabel = new Map(
    parsed.content.map((item) => [item.formLabel, item]),
  );
  for (const [formLabel, rawSection] of Object.entries(rawContentMap)) {
    const parsedItem = parsedByLabel.get(formLabel);
    const rawObj = asRecord(rawSection);
    const parsedObj =
      parsedItem != null
        ? (JSON.parse(JSON.stringify(parsedItem)) as Record<string, unknown>)
        : {};
    const parsedForDiff = alignParsedGridCellForOpaqueDiff(rawObj, parsedObj);
    paths.push(
      ...diffKeysDroppedFromParsed(
        rawObj,
        parsedForDiff,
        `content.${formLabel}`,
      ),
    );
  }

  if (parsed.advisorContent != null && root.advisorContent != null) {
    paths.push(
      ...diffKeysDroppedFromParsed(
        asRecord(root.advisorContent),
        JSON.parse(JSON.stringify(parsed.advisorContent)) as Record<
          string,
          unknown
        >,
        "advisorContent",
      ),
    );
  }

  return [...new Set(paths)].sort((a, b) => a.localeCompare(b));
};
